import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Star, 
  Phone, 
  Mail, 
  Calendar, 
  DollarSign,
  MessageCircle,
  Eye,
  Plus,
  Search,
  Filter,
  Award,
  CheckCircle,
  Send,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '../../ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Input } from '../../ui/input';

const MyTrainers = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageText, setMessageText] = useState('');

  // Mock trainers data
  const trainers = [
    {
      id: 1,
      name: 'Dr. Sarah Johnson',
      avatar: null,
      specialization: 'Leadership Development',
      rating: 4.8,
      experience: '12 years',
      status: 'active',
      sessionsCompleted: 8,
      totalRevenue: 45000,
      lastSession: '2024-09-25',
      nextSession: '2024-10-02',
      email: 'sarah.johnson@email.com',
      phone: '+1 (555) 123-4567',
      expertise: ['Leadership', 'Team Building', 'Strategic Planning'],
      certifications: ['ICF Certified', 'PMP', 'Leadership Coach'],
      location: 'New York, NY'
    },
    {
      id: 2,
      name: 'Michael Chen',
      avatar: null,
      specialization: 'Technical Training',
      rating: 4.6,
      experience: '8 years',
      status: 'active',
      sessionsCompleted: 12,
      totalRevenue: 32000,
      lastSession: '2024-09-22',
      nextSession: '2024-10-05',
      email: 'michael.chen@email.com',
      phone: '+1 (555) 987-6543',
      expertise: ['Software Development', 'Agile', 'DevOps'],
      certifications: ['AWS Certified', 'Scrum Master', 'Azure Expert'],
      location: 'San Francisco, CA'
    },
    {
      id: 3,
      name: 'Emily Rodriguez',
      avatar: null,
      specialization: 'Project Management',
      rating: 4.9,
      experience: '10 years',
      status: 'inactive',
      sessionsCompleted: 6,
      totalRevenue: 28000,
      lastSession: '2024-08-15',
      nextSession: null,
      email: 'emily.rodriguez@email.com',
      phone: '+1 (555) 456-7890',
      expertise: ['Project Management', 'Risk Management', 'Quality Assurance'],
      certifications: ['PMP', 'Six Sigma', 'PRINCE2'],
      location: 'Austin, TX'
    },
    {
      id: 4,
      name: 'James Wilson',
      avatar: null,
      specialization: 'Team Development',
      rating: 4.7,
      experience: '6 years',
      status: 'preferred',
      sessionsCompleted: 4,
      totalRevenue: 18000,
      lastSession: '2024-09-20',
      nextSession: '2024-10-08',
      email: 'james.wilson@email.com',
      phone: '+1 (555) 321-0987',
      expertise: ['Team Building', 'Communication', 'Conflict Resolution'],
      certifications: ['Certified Team Coach', 'NLP Practitioner'],
      location: 'Chicago, IL'
    },
    {
      id: 5,
      name: 'Lisa Zhang',
      avatar: null,
      specialization: 'Organizational Development',
      rating: 4.5,
      experience: '9 years',
      status: 'active',
      sessionsCompleted: 7,
      totalRevenue: 35000,
      lastSession: '2024-09-18',
      nextSession: '2024-10-12',
      email: 'lisa.zhang@email.com',
      phone: '+1 (555) 654-3210',
      expertise: ['Change Management', 'Culture Transformation', 'HR Strategy'],
      certifications: ['SHRM-CP', 'Change Management', 'Prosci Certified'],
      location: 'Seattle, WA'
    }
  ];

  const filteredTrainers = trainers.filter(trainer => {
    const matchesSearch = trainer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trainer.specialization.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trainer.expertise.some(exp => exp.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterStatus === 'all' || trainer.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'preferred': return 'bg-blue-100 text-blue-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'preferred': return <Star className="w-4 h-4" />;
      case 'inactive': return <Users className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const handleSendMessage = (trainer) => {
    console.log('=== MESSAGE BUTTON CLICKED ===');
    setSelectedTrainer(trainer);
    setMessageSubject('');
    setMessageText('');
    setShowMessageModal(true);
  };

  const handleSendMessageSubmit = async () => {
    if (!messageText.trim()) {
      alert('Please enter a message');
      return;
    }

    console.log('Sending message to:', selectedTrainer.name);
    console.log('Subject:', messageSubject);
    console.log('Message:', messageText);

    // TODO: Implement actual message sending API
    alert(`Message sent to ${selectedTrainer.name}!`);
    
    setShowMessageModal(false);
    setSelectedTrainer(null);
    setMessageSubject('');
    setMessageText('');
  };

  const handleBookSession = (trainer) => {
    console.log('=== handleBookSession clicked ===', trainer);
    const url = `/book-session/${trainer.id}`;
    console.log('Navigating to:', url);
    
    try {
      navigate(url);
    } catch (error) {
      console.error('Navigate error:', error);
      window.location.href = url;
    }
  };

  const handleViewProfile = (trainer) => {
    console.log('=== handleViewProfile clicked ===', trainer);
    const url = `/digitpro/${trainer.id}`;
    console.log('Navigating to:', url);
    
    try {
      navigate(url);
    } catch (error) {
      console.error('Navigate error:', error);
      window.location.href = url;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Trainers</h1>
            <p className="text-gray-600">Manage your preferred trainers and training partnerships</p>
          </div>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Find New Trainers
        </Button>
      </div>

      {/* Search and Filter Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, specialization, or expertise..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Trainers ({trainers.length})</option>
              <option value="active">Active ({trainers.filter(t => t.status === 'active').length})</option>
              <option value="preferred">Preferred ({trainers.filter(t => t.status === 'preferred').length})</option>
              <option value="inactive">Inactive ({trainers.filter(t => t.status === 'inactive').length})</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Trainers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTrainers.map((trainer) => (
          <Card key={trainer.id} className="hover:shadow-lg transition-shadow duration-200">
            <CardContent className="p-6">
              {/* Header with Avatar and Basic Info */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={trainer.avatar} alt={trainer.name} />
                    <AvatarFallback className="bg-blue-600 text-white font-semibold text-lg">
                      {trainer.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">{trainer.name}</h3>
                    <p className="text-blue-600 font-medium">{trainer.specialization}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <div className="flex items-center">
                        <Star className="w-4 h-4 text-yellow-500 mr-1" />
                        <span className="font-medium">{trainer.rating}</span>
                      </div>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-600 text-sm">{trainer.experience}</span>
                    </div>
                  </div>
                </div>
                <Badge className={`${getStatusColor(trainer.status)} flex items-center space-x-1`}>
                  {getStatusIcon(trainer.status)}
                  <span className="capitalize">{trainer.status}</span>
                </Badge>
              </div>

              {/* Performance Metrics */}
              <div className="grid grid-cols-3 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="font-bold text-lg text-blue-600">{trainer.sessionsCompleted}</div>
                  <div className="text-xs text-gray-600">Sessions</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-lg text-green-600">${trainer.totalRevenue.toLocaleString()}</div>
                  <div className="text-xs text-gray-600">Revenue</div>
                </div>
                <div className="text-center">
                  <div className="font-bold text-lg text-purple-600">{trainer.rating}</div>
                  <div className="text-xs text-gray-600">Rating</div>
                </div>
              </div>

              {/* Expertise Tags */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Expertise:</p>
                <div className="flex flex-wrap gap-1">
                  {trainer.expertise.slice(0, 3).map((skill, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                  {trainer.expertise.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{trainer.expertise.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>

              {/* Certifications */}
              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Certifications:</p>
                <div className="flex flex-wrap gap-1">
                  {trainer.certifications.slice(0, 2).map((cert, index) => (
                    <Badge key={index} className="bg-orange-100 text-orange-800 text-xs">
                      <Award className="w-3 h-3 mr-1" />
                      {cert}
                    </Badge>
                  ))}
                  {trainer.certifications.length > 2 && (
                    <Badge className="bg-orange-100 text-orange-800 text-xs">
                      +{trainer.certifications.length - 2} more
                    </Badge>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="mb-4 text-sm text-gray-600">
                <div className="flex items-center mb-1">
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="text-blue-600 hover:underline cursor-pointer">{trainer.email}</span>
                </div>
                <div className="flex items-center mb-1">
                  <Phone className="w-4 h-4 mr-2" />
                  <span>{trainer.phone}</span>
                </div>
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Last session: {new Date(trainer.lastSession).toLocaleDateString()}</span>
                </div>
                {trainer.nextSession && (
                  <div className="flex items-center mt-1">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-green-600 font-medium">
                      Next: {new Date(trainer.nextSession).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2 pt-3 border-t">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleViewProfile(trainer)}
                  className="flex-1"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View Profile
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSendMessage(trainer)}
                  className="flex-1"
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  Message
                </Button>
                {trainer.status === 'active' && (
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 flex-1"
                    onClick={() => handleBookSession(trainer)}
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Book
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredTrainers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No trainers found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterStatus !== 'all' 
                ? "Try adjusting your search or filter criteria"
                : "Start building your trainer network by finding and connecting with qualified trainers"
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <Button className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Find Trainers
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Trainer Network Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {trainers.filter(t => t.status === 'active').length}
              </div>
              <div className="text-sm text-blue-800">Active Trainers</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {trainers.reduce((sum, t) => sum + t.sessionsCompleted, 0)}
              </div>
              <div className="text-sm text-green-800">Total Sessions</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-600">
                ${trainers.reduce((sum, t) => sum + t.totalRevenue, 0).toLocaleString()}
              </div>
              <div className="text-sm text-purple-800">Total Investment</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {(trainers.reduce((sum, t) => sum + t.rating, 0) / trainers.length).toFixed(1)}
              </div>
              <div className="text-sm text-yellow-800">Average Rating</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <MessageCircle className="w-5 h-5" />
              <span>Send Message to {selectedTrainer?.name}</span>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Trainer Info */}
            {selectedTrainer && (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={selectedTrainer.avatar} alt={selectedTrainer.name} />
                  <AvatarFallback>{selectedTrainer.name?.charAt(0) || 'T'}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{selectedTrainer.name}</p>
                  <p className="text-sm text-gray-600">{selectedTrainer.specialization}</p>
                </div>
              </div>
            )}

            {/* Subject */}
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                placeholder="Enter subject..."
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                className="mt-2"
              />
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                rows={8}
                placeholder="Type your message here..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="mt-2"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowMessageModal(false)}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleSendMessageSubmit}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTrainers;